import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import type { HandbookList } from "features/handbookList/model/handbookList";
import { useLazyGetHandbooksListQuery } from "features/handbookList/api/handbooksListApi";

type Options = {
  code?: string;
  path?: string;
};

type HandbookItem = {
  code: string;
  nameRu: string;
  nameKy: string;
};

const RESERVED = new Set(["all", "all-handbooks", "all-services"]);

export function useNavTitle({ code, path }: Options) {
  const { i18n } = useTranslation();
  const location = useLocation();

  const [items, setItems] = useState<HandbookItem[]>([]);
  const [selectedService, setSelectedService] = useState<string | undefined>();

  const [fetchHandbooks, { data: handbooksData, isFetching: hbLoading }] =
    useLazyGetHandbooksListQuery();

  useEffect(() => {
    const [serviceFromUrl] = location.pathname.split("/").filter(Boolean);
    setSelectedService(serviceFromUrl || undefined);
  }, [location.pathname]);

  useEffect(() => {
    if (selectedService === undefined) {
      setItems([]);
      return;
    }

    const serviceCodes = RESERVED.has(selectedService) ? [] : [selectedService];

    fetchHandbooks({
      page: 1,
      size: 200,
      deleted: false,
      serviceCodes,
    });
  }, [selectedService, fetchHandbooks]);

  useEffect(() => {
    const raw: HandbookList[] = handbooksData?.res?.content ?? [];
    const normalized: HandbookItem[] = raw
      .filter((x) => typeof x.code === "string" && x.code!.trim() !== "")
      .map((x) => ({
        code: String(x.code),
        nameRu: String(x.nameRu ?? ""),
        nameKy: String(x.nameKy ?? ""),
      }));
    setItems(normalized);
  }, [handbooksData]);

  const currentDictionaryCode = useMemo(() => {
    if (code) return code;

    const segs = (path ?? location.pathname).split("/").filter(Boolean);
    return segs[segs.length - 1];
  }, [code, path, location.pathname]);

  const title = useMemo(() => {
    if (!currentDictionaryCode) return undefined;
    const hit = items.find((i) => i.code === currentDictionaryCode);
    if (!hit) return undefined;

    const isKy = i18n.language?.toLowerCase().startsWith("ky");
    return isKy ? hit.nameKy : hit.nameRu;
  }, [items, currentDictionaryCode, i18n.language]);

  return { title, loading: hbLoading };
}
